import { escapeHtml } from '../ui.js';

// Bulk bar — edit-patterns artboard 6f. "The same change to several rows."
//
// LAW (do not undo): the bulk bar is for FILING, NOT DECIDING. Its actions are
// only the ones that are safe in quantity — file to…, tag, mark read, attach to
// mail, download, assign to project. NEVER money, status, revision, permission,
// or anything that carries a reason step. The caller supplies the actions; keep
// them filing-only. Tables that have a bulk bar: Files, Mail, Expenses, Time,
// Procurement. Not Team, Projects, Clients or Invoices.
//
// ONE NAMED EXCEPTION (v4.23.10, Karan 30/10/2026 "need a delete option"): the
// Files folder table's "Move N to bin". It is not a decision about the work —
// nothing is deleted, Drive keeps the files for 30 days, the toast carries Undo
// — and it never acts from the bar alone: it opens the same ending confirm a
// single row does (files-bin.js, files-folder.js). No other table may add it.
//
// Geometry is verbatim 6f:
//  - Checkboxes live in a 26px first column that is NOT there at rest — the
//    caller reveals it only once one row is selected (via onSelectionChange).
//  - Selected rows take var(--nav-hover) with the bled shadow
//    box-shadow:-16px 0 0 var(--halo),16px 0 0 var(--halo).
//  - The bar rises from the bottom of the CONTENT area, centred, 56px tall,
//    var(--primary) with var(--primary-ink) text (the toast's pair, so it reads
//    in dark mode too — it was --ink-fill + fixed white, white-on-white in dark), 12px radius, box-shadow:0 14px 34px rgba(12,17,57,0.26).
//    It is absolutely positioned INSIDE `host` (the content column) so it never
//    reaches over the rail. The table gains 84px of bottom padding while the bar
//    is up so the last row is never covered (caller applies it on selection).
//  - Contents: count in white 13.5px/500, a hairline divider at 22% white, the
//    actions in var(--brand-light-grey), a spacer, then Clear as a 34px outlined
//    button (1px solid rgba(255,255,255,0.3)).
//  - Escape clears the selection.

// A single 15px checkbox cell the caller drops into a row's 26px first column.
// checked → blue filled box with ✓; unchecked → 1.5px var(--hair-strong) outline.
// Carries data-bulk-id so the caller can wire clicks → controller.toggle(id).
export function checkboxCell(id, checked) {
  const safe = escapeHtml(String(id == null ? '' : id));
  if (checked) {
    return `<span data-bulk-id="${safe}" role="checkbox" aria-checked="true" tabindex="0" `
      + `style="width:15px;height:15px;border:1.5px solid var(--link);border-radius:3px;`
      + `background:var(--brand-blue);color:var(--brand-white);font-size:10px;display:flex;`
      + `align-items:center;justify-content:center;cursor:pointer;">&#10003;</span>`;
  }
  return `<span data-bulk-id="${safe}" role="checkbox" aria-checked="false" tabindex="0" `
    + `style="width:15px;height:15px;border:1.5px solid var(--hair-strong);border-radius:3px;`
    + `cursor:pointer;"></span>`;
}

// The header checkbox for the first column. state ∈ 'none' | 'partial' | 'all'.
// 'partial' shows a dash (–), 'all' shows a check (✓), both blue-filled; 'none'
// is an empty grey outline. Carries data-bulk-all so the caller can wire it to
// controller.setAll(ids) / controller.clear().
export function headerCheckbox(state) {
  if (state === 'all' || state === 'partial') {
    const glyph = state === 'partial' ? '&#8211;' : '&#10003;';
    return `<span data-bulk-all="${state}" role="checkbox" aria-checked="${state === 'all' ? 'true' : 'mixed'}" `
      + `tabindex="0" style="width:15px;height:15px;border:1.5px solid var(--link);`
      + `border-radius:3px;background:var(--brand-blue);color:var(--brand-white);font-size:10px;`
      + `display:flex;align-items:center;justify-content:center;cursor:pointer;">${glyph}</span>`;
  }
  return `<span data-bulk-all="none" role="checkbox" aria-checked="false" tabindex="0" `
    + `style="width:15px;height:15px;border:1.5px solid var(--hair-strong);border-radius:3px;`
    + `cursor:pointer;"></span>`;
}

// A small controller each table drives itself, since every table renders its own
// rows. The controller owns the Set of selected ids and renders/updates the
// rising bar (appended to `host`, absolutely positioned bottom-centre within it —
// so `host` should be position:relative; it is forced if static). On every change
// onSelectionChange(selectedIdSet) fires so the caller can (a) reveal the 26px
// checkbox column, (b) tint selected rows blue-50, (c) set the table's 84px
// bottom padding. When the selection empties, the bar is removed and the caller
// resets those. Escape clears the selection while any row is selected.
//
//   createBulkBar(host, {
//     actions,           // [{ label | label(n), onRun(selectedIds) }] — FILING actions only
//     onSelectionChange, // optional (selectedIdSet) => void
//     sticky,            // optional: the bar sticks to the bottom of a SCROLLING host
//                        // (an overlay) instead of sitting at the host's foot
//   })
//   → { toggle(id), isSelected(id), selected(), clear(), setAll(ids), render() }
export function createBulkBar(host, opts) {
  const options = opts || {};
  const actions = Array.isArray(options.actions) ? options.actions : [];
  const onSelectionChange = typeof options.onSelectionChange === 'function'
    ? options.onSelectionChange
    : null;

  const chosen = new Set();
  let barEl = null;

  const sticky = options.sticky === true;
  if (!sticky && host && host.style && getComputedStyle(host).position === 'static') {
    host.style.position = 'relative';
  }

  function fireChange() {
    if (onSelectionChange) {
      try { onSelectionChange(new Set(chosen)); } catch (_) { /* caller's problem */ }
    }
  }

  function onKey(e) {
    if (e.key === 'Escape' && chosen.size) { e.preventDefault(); clear(); }
  }

  function onBarClick(e) {
    const clearEl = e.target.closest('[data-bulk-clear]');
    if (clearEl) { e.preventDefault(); clear(); return; }
    const actEl = e.target.closest('[data-bulk-action]');
    if (actEl && barEl && barEl.contains(actEl)) {
      const i = Number(actEl.getAttribute('data-bulk-action'));
      const action = actions[i];
      if (action && typeof action.onRun === 'function') {
        e.preventDefault();
        try { action.onRun([...chosen]); } catch (_) { /* caller's problem */ }
      }
    }
  }

  function buildBar() {
    const bar = document.createElement('div');
    bar.setAttribute('data-bulk-bar', '');
    // Absolutely positioned inside the content column — centred, clear of the
    // rail. bottom:14px leaves 14px above/below within the caller's 84px pad.
    bar.style.cssText = (sticky
      ? 'position:sticky;bottom:14px;margin:14px auto 0;'
      : 'position:absolute;left:50%;bottom:14px;transform:translateX(-50%);')
      + 'width:760px;max-width:calc(100% - 48px);'
      + 'height:56px;background:var(--primary);color:var(--primary-ink);border-radius:12px;display:flex;'
      + 'align-items:center;padding:0 12px 0 22px;gap:18px;'
      + 'box-shadow:0 14px 34px rgba(12,17,57,0.26);z-index:20;';
    bar.addEventListener('click', onBarClick);
    bar.addEventListener('keydown', (e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target.closest('[data-bulk-action],[data-bulk-clear]')) onBarClick(e); });
    return bar;
  }

  function paintBar() {
    if (!barEl) return;
    const n = chosen.size;
    const acts = actions.map((a, i) => {
      const label = a && typeof a.label === 'function' ? a.label(n) : (a && a.label != null ? a.label : '');
      return `<span data-bulk-action="${i}" role="button" tabindex="0" style="font-size:13.5px;color:var(--primary-ink);`
        + `cursor:pointer;">${escapeHtml(label)}</span>`;
    }).join('');
    barEl.innerHTML =
      `<span style="font-size:13.5px;font-weight:500;color:var(--primary-ink);">`
      + `${escapeHtml(String(n))} selected</span>`
      + `<span style="width:1px;height:22px;background:var(--primary-ink);opacity:0.22;"></span>`
      + acts
      + `<span style="flex:1;"></span>`
      + `<span data-bulk-clear role="button" tabindex="0" style="height:34px;display:inline-flex;align-items:center;`
      + `padding:0 16px;border-radius:8px;border:1px solid var(--primary-ink);opacity:0.8;`
      + `font-size:12.5px;color:var(--primary-ink);cursor:pointer;">Clear</span>`;
  }

  function render() {
    if (chosen.size > 0) {
      // The table repainted over it (a caller that rebuilds its rows with
      // innerHTML takes the bar with them): put the same bar back.
      if (barEl && !barEl.isConnected && host) host.appendChild(barEl);
      if (!barEl) {
        barEl = buildBar();
        host.appendChild(barEl);
        document.addEventListener('keydown', onKey, true);
      }
      paintBar();
    } else if (barEl) {
      barEl.removeEventListener('click', onBarClick);
      barEl.remove();
      barEl = null;
      document.removeEventListener('keydown', onKey, true);
    }
  }

  function toggle(id) {
    const key = String(id);
    if (chosen.has(key)) chosen.delete(key); else chosen.add(key);
    fireChange();
    render();
  }

  function isSelected(id) {
    return chosen.has(String(id));
  }

  function selected() {
    return [...chosen];
  }

  function clear() {
    if (!chosen.size) return;
    chosen.clear();
    fireChange();
    render();
  }

  function setAll(ids) {
    chosen.clear();
    (Array.isArray(ids) ? ids : []).forEach((id) => chosen.add(String(id)));
    fireChange();
    render();
  }

  return { toggle, isSelected, selected, clear, setAll, render };
}
